import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconButton } from './Button';

/**
 * Dialogo modal (RNF15 / RNF16).
 *
 * Fecha no Escape e no clique fora; devolve o foco ao elemento que abriu; trava
 * a rolagem da pagina enquanto esta aberto. Em tela estreita ocupa a base da
 * tela como uma folha (sheet), que e onde o polegar alcanca.
 *
 * Nao e portal: renderiza no lugar, com `position: fixed` e a camada `z-drawer`
 * do sistema de design. Basta para o painel e evita um segundo mecanismo de
 * empilhamento.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly size?: 'md' | 'lg' | 'xl';
}): JSX.Element | null {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const ultimoFoco = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    ultimoFoco.current = document.activeElement;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const primeiro = panelRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    primeiro?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflowAnterior;
      if (ultimoFoco.current instanceof HTMLElement) {
        ultimoFoco.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const largura = size === 'xl' ? 'sm:max-w-4xl' : size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg';

  return (
    <div className="fixed inset-0 z-drawer flex items-end justify-center sm:items-center sm:p-6">
      <div aria-hidden="true" onMouseDown={onClose} className="absolute inset-0 bg-scrim/60 animate-fade-in" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descId}
        className={`relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-line bg-elevated shadow-float animate-fade-in sm:rounded-2xl ${largura}`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold text-ink">
              {title}
            </h2>
            {description === undefined ? null : (
              <p id={descId} className="mt-0.5 text-sm text-ink-muted">
                {description}
              </p>
            )}
          </div>
          <IconButton icon="x" label="Fechar" onClick={onClose} size="sm" />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer === undefined ? null : <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
